import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Autocomplete,
  Chip,
  MenuItem,
  FormHelperText,
  Alert
} from '@mui/material';
import { Send as SendIcon, AutoAwesome as SparkleIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { brandColors } from '../../theme/theme';

/**
 * LoglineAnalysisForm Component
 * 
 * Form for submitting film loglines for AI analysis.
 * Includes multi-genre selection, distribution strategy, and validation.
 */

// Suggested genre options
const SUGGESTED_GENRES = [
  'Comedy',
  'Drama',
  'Thriller',
  'Horror',
  'Action',
  'Sci-Fi',
  'Romance',
  'Mystery',
  'Fantasy',
  'Documentary',
  'Western',
  'Crime',
  'Musical',
  'Adventure',
  'Biography'
];

// Distribution strategy options (must match backend validation)
const DISTRIBUTION_OPTIONS = [
  { value: 'theatrical', label: 'Theatrical' },
  { value: 'streaming', label: 'Streaming' },
  { value: 'hybrid', label: 'Hybrid' }
];

const LoglineAnalysisForm = ({ onSubmit, loading = false }) => {
  const { userProfile } = useAuth();
  
  // Form state
  const [logline, setLogline] = useState('');
  const [genres, setGenres] = useState([]);
  const [distributionStrategy, setDistributionStrategy] = useState('still_deciding');
  
  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Character limits
  const MIN_LOGLINE_LENGTH = 10;
  const MAX_LOGLINE_LENGTH = 500;

  // Validate form
  const validate = () => {
    const newErrors = {};

    // Logline validation
    if (!logline.trim()) {
      newErrors.logline = 'Logline is required';
    } else if (logline.trim().length < MIN_LOGLINE_LENGTH) {
      newErrors.logline = `Logline must be at least ${MIN_LOGLINE_LENGTH} characters`;
    } else if (logline.length > MAX_LOGLINE_LENGTH) {
      newErrors.logline = `Logline must not exceed ${MAX_LOGLINE_LENGTH} characters`;
    }

    // Genre validation
    if (genres.length === 0) {
      newErrors.genres = 'Please select at least one genre';
    }

    // Distribution strategy validation
    if (!distributionStrategy) {
      newErrors.distributionStrategy = 'Please select a distribution strategy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    setTouched({
      logline: true,
      genres: true,
      distributionStrategy: true
    });

    // Validate
    if (!validate()) {
      return;
    }

    // Prepare submission data
    const submissionData = {
      logline: logline.trim(),
      genre: genres.join(', '), // Convert array to comma-separated string
      distribution_strategy: distributionStrategy,
      user_id: userProfile?.email || userProfile?.displayName || 'unknown_user',
      force_new: false
    };

    // Call parent's onSubmit handler
    if (onSubmit) {
      onSubmit(submissionData);
    }
  };

  // Handle field blur (mark as touched)
  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    validate();
  };

  // Get character count color
  const getCharCountColor = () => {
    if (logline.length > MAX_LOGLINE_LENGTH) return brandColors.secondary;
    if (logline.length > MAX_LOGLINE_LENGTH * 0.9) return '#f57c00';
    return brandColors.primary;
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        maxWidth: 800,
        mx: 'auto',
        borderTop: `4px solid ${brandColors.secondary}`
      }}
    >
      {/* Form Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <SparkleIcon sx={{ fontSize: 48, color: brandColors.secondary, mb: 1 }} />
        <Typography
          variant="h5"
          sx={{
            color: brandColors.primary,
            fontWeight: 600,
            mb: 1
          }}
        >
          Submit Your Logline for Analysis
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: brandColors.primary,
            opacity: 0.7
          }}
        >
          Our AI will analyze your concept, provide comparable films, market insights, and recommendations
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        {/* Logline Field */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: brandColors.primary,
              fontWeight: 600,
              mb: 1
            }}
          >
            Logline *
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="E.g., When two megastars start dating, their assistants' sanity, and career, hinge on a cutthroat comedy of cover-ups, sabotage, and survival."
            value={logline}
            onChange={(e) => setLogline(e.target.value)}
            onBlur={() => handleBlur('logline')}
            error={touched.logline && Boolean(errors.logline)}
            helperText={touched.logline && errors.logline}
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: brandColors.secondary
                },
                '&.Mui-focused fieldset': {
                  borderColor: brandColors.secondary
                }
              }
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <FormHelperText>
              Describe your film concept in one compelling sentence
            </FormHelperText>
            <FormHelperText
              sx={{
                color: getCharCountColor(),
                fontWeight: logline.length > MAX_LOGLINE_LENGTH ? 600 : 400
              }}
            >
              {logline.length} / {MAX_LOGLINE_LENGTH}
            </FormHelperText>
          </Box>
        </Box>

        {/* Genre Field */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: brandColors.primary,
              fontWeight: 600,
              mb: 1
            }}
          >
            Genre(s) *
          </Typography>
          <Autocomplete
            multiple
            freeSolo
            options={SUGGESTED_GENRES}
            value={genres}
            onChange={(event, newValue) => {
              setGenres(newValue);
            }}
            onBlur={() => handleBlur('genres')}
            disabled={loading}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...chipProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    label={option}
                    {...chipProps}
                    sx={{
                      backgroundColor: 'rgba(255, 68, 73, 0.1)',
                      color: brandColors.secondary,
                      '& .MuiChip-deleteIcon': {
                        color: brandColors.secondary,
                        '&:hover': {
                          color: brandColors.primary
                        }
                      }
                    }}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={genres.length === 0 ? "Select or type genres..." : ""}
                error={touched.genres && Boolean(errors.genres)}
                helperText={touched.genres && errors.genres}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: brandColors.secondary
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: brandColors.secondary
                    }
                  }
                }}
              />
            )}
          />
          <FormHelperText>
            Select from suggestions or type your own (press Enter to add)
          </FormHelperText>
        </Box>

        {/* Distribution Strategy Field */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: brandColors.primary,
              fontWeight: 600,
              mb: 1
            }}
          >
            Distribution Strategy *
          </Typography>
          <TextField
            fullWidth
            select
            value={distributionStrategy}
            onChange={(e) => setDistributionStrategy(e.target.value)}
            onBlur={() => handleBlur('distributionStrategy')}
            error={touched.distributionStrategy && Boolean(errors.distributionStrategy)}
            helperText={touched.distributionStrategy ? errors.distributionStrategy : "How do you plan to release this film?"}
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: brandColors.secondary
                },
                '&.Mui-focused fieldset': {
                  borderColor: brandColors.secondary
                }
              }
            }}
          >
            {DISTRIBUTION_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Info Alert */}
        {!loading && (
          <Alert
            severity="info"
            sx={{
              mb: 3,
              '& .MuiAlert-icon': {
                color: brandColors.secondary
              }
            }}
          >
            Analysis typically takes 1-2 minutes. You'll receive a comprehensive report with market insights, comparable films, and strategic recommendations.
          </Alert>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={loading}
          endIcon={<SendIcon />}
          sx={{
            backgroundColor: brandColors.secondary,
            color: brandColors.white,
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: brandColors.primary
            },
            '&:disabled': {
              backgroundColor: brandColors.primary,
              opacity: 0.5,
              color: brandColors.white
            }
          }}
        >
          {loading ? 'Analyzing...' : 'Analyze Logline'}
        </Button>
      </form>
    </Paper>
  );
};

export default LoglineAnalysisForm;

